import { ProfileRoles } from '@bp/pim-auth-constants';
import { AddIcon, CheckIcon, Grid, GridColumn, GridRow, Modal, SpinnerIcon } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useAssignments } from 'components/Assignments/hooks/useAssignments';
import { AssignmentsActiveTable } from 'components/Assignments/tables/AssignmentsActiveTable';
import { GroupMessageModal } from 'components/Messages/modals/GroupMessageModal';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useRequireParams } from 'hooks/useRequireParams';
import { useMessageCommands } from 'matrix/commands/useMessageCommands';
import { NotificationCountType } from 'matrix-js-sdk';
import { Suspense, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { CONNECT_DATE_TEMPLATE } from 'utils/connectLib';
import { AnnouncementForm, type AnnouncementFormType } from '../../../../components/AnnouncementForm/AnnouncementForm';
import { AnnouncementsList } from '../../../../components/AnnouncementsList/AnnouncementsList';
import { BpCard } from '../../../../components/BpCard/BpCard';
import { TodayEventList } from '../../../../components/Events/components/TodayEventList';
import { MessageLinkButtons } from '../../../../components/LinkButtons/MessageLinkButtons';
import { TeachingUnitsTree } from '../../../../components/TeachingUnitsTree/TeachingUnitsTree';
import { TeachingUnitContext } from '../../../../context/TeachingUnitContext';
import { useGetAnnouncements } from '../../../../hooks/matrix/useGetAnnouncements';
import { useGetMatrixRoomByName } from '../../../../hooks/matrix/useGetMatrixRoomByName';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { redirectToDmMessagePage } from '../../../../matrix/utils/redirectToDmMessagePage';
import { backendApi } from '../../../../utils/backendApi';
import { NotificationReason, RoomNames } from '../../../../utils/matrixClient';
import { showErrorToast } from '../../../../utils/showErrorToast';

const CourseOverviewTeacherContentPageQueryDocument = graphql(`
  query CourseOverviewTeacherContentPageData(
    $groupUuid: ID!
    $organizationUuid: ID!
    $eventWhere: CalendarEventWhere!
  ) {
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      name
      organization {
        uuid
        __typename
      }
      viewers {
        uuid
        __typename
        user {
          __typename
          uuid
          status
        }
      }
    }
    ...TodayEventListFields
    ...MessageLinkButtonsFields
    ...GroupMessageFields
    ...UseAvailableServicesForOrganizationFields
  }
`);

export const CourseOverviewTeacherContent = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { courseUuid } = useRequireParams(['courseUuid']);
  const organizationUuid = pimAuthClaims.getOrganization().uuid;

  const [announcementModal, setAnnouncementModal] = useState<boolean>(false);
  const [messageModalOpen, setMessageModalOpen] = useState<boolean>(false);

  const { activeAssignments } = useAssignments({ courseUuid });

  const { getRoom } = useGetMatrixRoomByName();
  const { markAsRead } = useMessageCommands();
  const { client } = useMatrixClient();

  const navigate = useNavigate();
  const [{ data, error }, refetch] = useQuery({
    query: CourseOverviewTeacherContentPageQueryDocument,
    variables: {
      groupUuid: courseUuid,
      organizationUuid,
      eventWhere: {
        AND: [
          { holderConnection: { Group: { node: { uuid: courseUuid } } } },
          { days_SOME: { date_CONTAINS: dayjs().format(CONNECT_DATE_TEMPLATE) } },
        ],
      },
    },
    pause: !courseUuid,
  });

  const perms = usePermissionChecker();

  const currentCourse = data?.groups[0];

  const room = getRoom(`${currentCourse?.organization.uuid}_${courseUuid}`, RoomNames.Group);
  const _hasUnread = room ? room.getRoomUnreadNotificationCount(NotificationCountType.Total) > 0 : false;

  const [hasUnread, setHasUnread] = useState<boolean>(false);

  useEffect(() => {
    setHasUnread(_hasUnread);
  }, [_hasUnread]);

  const [loading, setLoading] = useState(false);
  const announcements = useGetAnnouncements(room);

  const { teachingUnitUuid, setTeachingUnitUuid } = useContext(TeachingUnitContext);

  const groupSubject = {
    uuid: courseUuid,
    organization: { uuid: currentCourse?.organization.uuid ?? '' },
  };

  const handleAnnouncementFormSubmit = async (values?: AnnouncementFormType) => {
    if (values) {
      try {
        setLoading(true);
        const roomId = room?.summary?.roomId;

        if (!roomId) {
          throw new Error('Could not find room');
        }

        if (!client) {
          throw new Error('Could not get Matrix Client');
        }

        if (!currentCourse?.name) {
          throw new Error('Could not get coursename');
        }

        const resp = await backendApi.notify(
          {
            type: NotificationReason.CourseAnnouncement,
            content: values.message,
            title: values.title,
            groupUuid: courseUuid,
          },
          { roomId, groupName: currentCourse?.name, client },
        );

        if (!resp.ok) throw new Error(resp.statusText);

        setAnnouncementModal(false);
      } catch (error) {
        showErrorToast({
          name: '',
          graphQLErrors: [],
          message: error as string,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  function handleMessageAll() {
    setMessageModalOpen(true);
  }
  async function handleMessage(uuid: string, role: ProfileRoles) {
    await redirectToDmMessagePage({
      groupUuid: courseUuid,
      targetProfileUuid: uuid,
      targetProfileOrganizationUuid: '',
      targetProfileRole: role,
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      context: 'course',
      permissionChecker: perms,
      navigate,
    });
  }
  error && showErrorToast(error);

  return (
    <>
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)'>
          <GridColumn width={8}>
            <BpCard
              className='mb-7'
              noPadding
              fixedHeight='360px'
              header={{
                headline: t('assignments.active'),
                subHeadline: activeAssignments?.length.toString(),
              }}
            >
              <AssignmentsActiveTable onNavigate={(uuid) => navigate(`/courses/${courseUuid}/assignments/${uuid}`)} />
            </BpCard>
            <BpCard
              canScroll
              fixedHeight='360px'
              noPadding
              hideActionTextBreakpoint='medium'
              header={{
                headline: t('announcements.title'),
                actions:
                  perms?.canCreateCourseAnnouncement(groupSubject) && room
                    ? [
                        {
                          text: t('common.mark'),
                          hierarchy: 'ghost',
                          disabled: !hasUnread,
                          icon: <CheckIcon />,
                          callback: async () => {
                            setHasUnread(false);
                            await markAsRead(room?.roomId);
                          },
                        },
                        {
                          text: t('announcements.new'),
                          icon: <AddIcon />,
                          callback: () => {
                            setAnnouncementModal(true);
                          },
                        },
                      ]
                    : [
                        {
                          text: t('announcements.new'),
                          icon: <AddIcon />,
                          disabled: true,
                        },
                      ],
              }}
            >
              <AnnouncementsList
                announcements={announcements}
                hasPermissionToDelete={perms?.canCreateCourseAnnouncement(groupSubject) ?? false}
              />
            </BpCard>
          </GridColumn>
          <GridColumn width={4}>
            <BpCard className='pb-6   mb-7' isEmbedded={true}>
              <TodayEventList
                query={data}
                permissionChecker={perms}
                refetchData={() => refetch({ requestPolicy: 'network-only' })}
              />
            </BpCard>

            <BpCard header={{ headline: t('teachingUnits.title') }} className='mb-7' alwaysCollapsible>
              <TeachingUnitsTree
                courseUuid={courseUuid}
                activeTeachingUnitUuid={teachingUnitUuid}
                onTeachingUnitSelect={(uuid: string) => {
                  setTeachingUnitUuid(uuid);
                  navigate(`/courses/${courseUuid}/lessons/${uuid}`);
                }}
              />
            </BpCard>
            <MessageLinkButtons onMessageAll={handleMessageAll} onMessage={handleMessage} query={data} />
          </GridColumn>
        </GridRow>
      </Grid>

      {room && (
        <Modal
          isOpen={announcementModal}
          onRequestClose={() => setAnnouncementModal(false)}
          isFormModal
          hideFooter
          title={t('announcements.new')}
          width='s'
        >
          <Suspense fallback={<SpinnerIcon className={'svg-icon large spinning'} />}>
            <AnnouncementForm
              loading={loading}
              onSubmit={handleAnnouncementFormSubmit}
              closeModal={() => setAnnouncementModal(false)}
            />
          </Suspense>
        </Modal>
      )}

      <GroupMessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        groupUuid={courseUuid}
        query={data}
      />
    </>
  );
};
